import { BadgeCheck, Pencil } from 'lucide-react';
import { useId } from 'react';

import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';

import type { AssignmentScore, GradebookAssignment } from './gradebookTypes';
import { buildCoachUnitKey, buildQuestionMetaKey, formatGradeValue } from './gradebookTypes';
import { ScoreCell } from './ScoreCell';
import type { FullCreditState } from './useGradebook';
import styles from './AssignmentScoreCard.module.css';

interface FullCreditProps {
  onGive: () => void;
  onUndo: () => void;
  state: FullCreditState;
}

interface AssignmentScoreCardProps {
  assignment: GradebookAssignment;
  assignmentScore: AssignmentScore | null;
  editedScores: Record<string, string>;
  // The "Full credit" strip, for the card being edited.
  fullCredit?: FullCreditProps;
  isEditing: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onEdit: () => void;
  onSave: () => void;
  onScoreChange: (questionKey: string, value: string) => void;
  saveError?: string | null;
  scoreErrors: Record<string, string>;
}

function findQuestionScore(
  assignmentScore: AssignmentScore | null,
  sectionId: string,
  questionIndex: number,
  kind: 'homework' | 'preLecture',
) {
  const questions =
    kind === 'preLecture'
      ? assignmentScore?.preLectureQuestionScores
      : assignmentScore?.questionScores;

  return (
    questions?.find(
      (question) =>
        question.index === questionIndex &&
        question.sectionId === sectionId &&
        question.kind === kind,
    ) ?? null
  );
}

function getSummaryLabel(score: number | null | undefined, possible: number) {
  if (possible <= 0) {
    return '—';
  }

  if (score === null || score === undefined) {
    return `— / ${formatGradeValue(possible)}`;
  }

  return `${formatGradeValue(score)} / ${formatGradeValue(possible)}`;
}

const FULL_CREDIT_TEXT: Record<FullCreditState, string> = {
  applied: 'Every score is set to its maximum. Save to keep it.',
  available:
    'Sets every score in this assignment to its maximum. You can still change any score before you save.',
  complete: 'Every score is already at its maximum.',
};

/**
 * Full credit for the whole assignment in one step. It fills the draft only:
 * nothing is stored until the card's Save, and until a score is changed by
 * hand the same button undoes it, so focus never has to move.
 */
function FullCreditStrip({ onGive, onUndo, state }: FullCreditProps) {
  const titleId = useId();
  const applied = state === 'applied';

  return (
    <div
      aria-labelledby={titleId}
      className={styles.fullCredit}
      data-state={state}
      role="group"
    >
      <span aria-hidden="true" className={styles.fullCreditIcon}>
        <BadgeCheck size={20} strokeWidth={2} />
      </span>
      <div className={styles.fullCreditText}>
        <p className={styles.fullCreditTitle} id={titleId}>
          Full credit
        </p>
        <p aria-live="polite" className={styles.fullCreditStatus}>
          {FULL_CREDIT_TEXT[state]}
        </p>
      </div>
      <Button
        className={styles.fullCreditButton}
        disabled={state === 'complete'}
        onClick={applied ? onUndo : onGive}
        variant="secondary"
      >
        {applied ? 'Undo full credit' : 'Give full credit'}
      </Button>
    </div>
  );
}

export function AssignmentScoreCard({
  assignment,
  assignmentScore,
  editedScores,
  fullCredit,
  isEditing,
  isSaving,
  onCancel,
  onEdit,
  onSave,
  onScoreChange,
  saveError,
  scoreErrors,
}: AssignmentScoreCardProps) {
  return (
    <Card className={styles.card} variant="elevated">
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <h2 className={styles.title}>{assignment.title}</h2>
        </div>
        <div className={styles.actions}>
          {isEditing ? (
            <>
              <Button disabled={isSaving} onClick={onCancel} variant="secondary">
                Cancel
              </Button>
              <Button disabled={isSaving} onClick={onSave} variant="primary">
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <Button onClick={onEdit} size="small" variant="secondary">
              <Pencil aria-hidden="true" size={14} strokeWidth={2} />
              Edit
            </Button>
          )}
        </div>
      </div>

      {isEditing && fullCredit ? <FullCreditStrip {...fullCredit} /> : null}

      {assignment.hasPreLecture ? (
        <section className={styles.scoreBlock}>
          <div className={styles.scoreBlockHeader}>
            <h3 className={styles.scoreBlockTitle}>Pre-Lecture</h3>
            <p className={styles.summary}>
              {getSummaryLabel(
                assignmentScore?.preLectureScore,
                assignment.preLectureMaxScore,
              )}
            </p>
          </div>
          <div className={styles.questionGrid} data-editing={isEditing ? 'true' : undefined}>
            {assignment.sections.flatMap((section) =>
              section.preLectureQuestions
                .filter((question) => question.isAssigned)
                .map((question) => {
                const questionKey = buildQuestionMetaKey(question);
                const inputId = `${assignment.componentUid}-${questionKey}`;
                const questionScore = findQuestionScore(
                  assignmentScore,
                  question.sectionId,
                  question.index,
                  'preLecture',
                );

                return (
                  <div className={styles.scoreItem} key={questionKey}>
                    <ScoreCell
                      error={scoreErrors[questionKey]}
                      inputId={inputId}
                      isEditing={isEditing}
                      label={question.label}
                      maxScore={question.maxScore}
                      onChange={(value) => onScoreChange(questionKey, value)}
                      value={
                        isEditing
                          ? editedScores[questionKey] ?? '0'
                          : questionScore?.score ?? null
                      }
                    />
                    {!isEditing && questionScore?.adjusted ? (
                      <span className={styles.adjusted}>Adjusted</span>
                    ) : null}
                  </div>
                );
              }),
            )}
          </div>
        </section>
      ) : null}

      {assignment.hasHomework ? (
        <section className={styles.scoreBlock}>
          <div className={styles.scoreBlockHeader}>
            <h3 className={styles.scoreBlockTitle}>Homework</h3>
            <p className={styles.summary}>
              {getSummaryLabel(
                assignmentScore?.homeworkScore,
                assignment.homeworkMaxScore,
              )}
            </p>
          </div>
          <div className={styles.questionGrid} data-editing={isEditing ? 'true' : undefined}>
            {assignment.sections.flatMap((section) =>
              section.homeworkQuestions
                .filter((question) => question.isAssigned)
                .map((question) => {
                const questionKey = buildQuestionMetaKey(question);
                const inputId = `${assignment.componentUid}-${questionKey}`;
                const questionScore = findQuestionScore(
                  assignmentScore,
                  question.sectionId,
                  question.index,
                  'homework',
                );

                return (
                  <div className={styles.scoreItem} key={questionKey}>
                    <ScoreCell
                      error={scoreErrors[questionKey]}
                      inputId={inputId}
                      isEditing={isEditing}
                      label={question.label}
                      maxScore={question.maxScore}
                      onChange={(value) => onScoreChange(questionKey, value)}
                      value={
                        isEditing
                          ? editedScores[questionKey] ?? '0'
                          : questionScore?.score ?? null
                      }
                    />
                    {!isEditing && questionScore?.adjusted ? (
                      <span className={styles.adjusted}>Adjusted</span>
                    ) : null}
                  </div>
                );
              }),
            )}
          </div>
        </section>
      ) : null}

      {assignment.hasParticipation ? (
        <section className={styles.scoreBlock}>
          <div className={styles.scoreBlockHeader}>
            <h3 className={styles.scoreBlockTitle}>Coach</h3>
            <p className={styles.summary}>
              {getSummaryLabel(
                assignmentScore?.participationScore,
                assignment.participationMaxScore,
              )}
            </p>
          </div>
          <div className={styles.questionGrid} data-editing={isEditing ? 'true' : undefined}>
            {(assignment.coachUnits ?? []).map((unit) => {
              const unitKey = buildCoachUnitKey(unit.uid);
              const unitScore = assignmentScore?.coachUnitScores?.find(
                (score) => score.unitUid === unit.uid,
              );

              return (
                <div className={styles.scoreItem} key={unitKey}>
                  <ScoreCell
                    error={scoreErrors[unitKey]}
                    inputId={`${assignment.componentUid}-${unitKey}`}
                    isEditing={isEditing}
                    label={unit.label}
                    maxScore={unit.points}
                    onChange={(value) => onScoreChange(unitKey, value)}
                    value={isEditing ? editedScores[unitKey] ?? '0' : unitScore?.score ?? null}
                  />
                  {!isEditing && unitScore?.adjusted ? (
                    <span className={styles.adjusted}>Adjusted</span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {isEditing && saveError ? (
        <p className={styles.error} role="alert">
          {saveError}
        </p>
      ) : null}
    </Card>
  );
}
